import React from 'react';
import {injectIntl, FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import {Radio, Collapse, Row, Col, Checkbox} from 'antd';

import LineBarChart from './line-bar-chart';
import PieChart from './pie-chart';
import { Svg } from '../../utils/Svg';

const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;
const Panel = Collapse.Panel;

const metricWithPopover = [
  "lead-age",
  "lead-duration",
  "consumer-origin-by-ip",
  "consumer-origin-by-device",
  "lead-closure",
  "lead-closure-relative",
];

class MainChart extends React.Component {

  static propTypes = {
    metric: PropTypes.string.isRequired,
    dateFrom: PropTypes.object.isRequired,
    dateTo: PropTypes.object.isRequired,
    groups: PropTypes.array,
    projects: PropTypes.array,
    labelType: PropTypes.string,
    labelValues: PropTypes.object,
    modes: PropTypes.array,
    scaling: PropTypes.bool
  }

  static defaultProps = {
    scaling: false
  }

  constructor(props) {
    super(props)
    this.state = {
      mode: null, //bar, line, pie
      unit: 'count', // count, percent
      showScore: false,
      scoringTypes: ['good'], // good, low, mid, high
    }
    if(props.initialState)
      this.state = props.initialState
  }

  componentWillUnmount() {
    if(this.props.setInitialState) this.props.setInitialState({[this.props.metric]: {...this.state}})
  }

  // Push 'type' into state when it hasn't or delete it
  toggleScoringType = type => {
    const { scoringTypes } = this.state;
    const newScoringTypes = ~scoringTypes.indexOf(type)
      ? scoringTypes.filter(i => i !== type)
      : [...scoringTypes, type];
    this.setState({ scoringTypes: newScoringTypes.length? newScoringTypes: ["good"] });
  }


  onModeChange = e => {
    e.stopPropagation();
    this.setState({mode: e.target.value});
  }

  onUnitChange = e => {
    e.stopPropagation();
    this.setState({unit: e.target.value});
  }

  onModeClick = (e) => {
    // prevent collapce :(
    e.stopPropagation();
  }


  getModes() {
    let {modes, metric} = this.props;
    if(metric === 'leads-gfm') return []
    if(this.state.showScore) return ['line', 'bar', 'pie']
    if (modes && modes.length) {
      return modes
    }
    modes = ['line', 'pie'];
    const noPieMetrics = [
      'lead-lineage', 'lead-lineage-you', 'lead-lineage-other',
      'lead-closure', 'lead-closure-relative'
    ]
    if (~noPieMetrics.indexOf(metric)) {
      modes = ['line', 'bar']
    }
    const allType = [ 'consumefr-origin-by-ip', 'consumer-origin-by-device'];
    if (~allType.indexOf(metric)) {
      modes = ['line', 'bar', 'pie']
    }
    if (metric === 'lead-age') {
      modes = ['line', 'pie']
    }
    return modes
  }

  getMode(modes) {
    if (!modes) {
      modes = this.getModes();
    }
    const mode0 = modes && modes[0];
    const mode = this.state.mode || mode0 || 'line';
    return mode;
  }

  renderScoreCheckbox = showTitles => {
    const {scoringTypes} = this.state
    if (this.state.showScore || this.props.metric !== "leads")
      return (
        <span>
          <Checkbox
            className="score_checkbox score_checkbox--good"
            onChange={() => this.toggleScoringType("good")}
            checked={~scoringTypes.indexOf("good")}
            disabled={scoringTypes.length === 1 && scoringTypes[0] === "good"}
          >
            {showTitles && (
              <FormattedMessage id="analytics.score.good" defaultMessage="Good" />
            )}
          </Checkbox>
          <Checkbox
            className="score_checkbox score_checkbox--low"
            onChange={() => this.toggleScoringType("low")}
            checked={~scoringTypes.indexOf("low")}
          >
            {showTitles && (
              <FormattedMessage
                id="analytics.score.low"
                defaultMessage="Low risk"
              />
            )}
          </Checkbox>
          <Checkbox
            className="score_checkbox score_checkbox--mid"
            onChange={() => this.toggleScoringType("mid")}
            checked={~scoringTypes.indexOf("mid")}
          >
            {showTitles && (
              <FormattedMessage
                id="analytics.score.mid"
                defaultMessage="Middle risk"
              />
            )}
          </Checkbox>
          <Checkbox
            className="score_checkbox score_checkbox--high"
            onChange={() => this.toggleScoringType("high")}
            checked={~scoringTypes.indexOf("high")}
          >
            {showTitles && (
              <FormattedMessage
                id="analytics.score.high"
                defaultMessage="High risk"
              />
            )}
          </Checkbox>
        </span>
      )
    else return null
  }

  renderHeaderCheckbox = () => {
    if (this.props.metric === "leads")
      return (
        <span onClick={e => e.stopPropagation()}>
          <Checkbox
            className="score_checkbox score_checkbox--toggler"
            onChange={() =>
              this.setState(({ showScore, mode }) => ({
                mode: mode === "pie" ? "line" : mode, //fix toggle to inactive mode
                showScore: !showScore,
                scoringTypes: ["good"]
              }))
            }
            checked={!this.state.showScore}
            disabled={this.state.loadingLeadsChart}
          >
            <FormattedMessage id="analytics.score.total" defaultMessage="Total" />
          </Checkbox>
          {this.renderScoreCheckbox(this.props.metric === "leads")}
        </span>
      )
    else if (~['lead-closure', 'lead-closure-relative'].indexOf(this.props.metric))
      return (
        <span onClick={e => e.stopPropagation()} style={{marginLeft: 5}}>
          {this.renderScoreCheckbox()}
        </span>
      )
    else return null
  }

  renderHeader() {
    return (
      <Row type='flex'>
        <Col style={{flex: 'auto', display: 'flex', alignItems: 'center'}}>
          {this.renderName()}
          {this.renderHeaderCheckbox()}
        </Col>
        <Col style={{marginLeft: 'auto'}}>
          {this.renderTools()}
        </Col>
      </Row>
    )
  }

  renderName() {
    const {header, metric} = this.props;
    if (header !== undefined && header !== null) {
      return header;
    }
    return (
      <span className="popover-container">
        <FormattedMessage id={'analytics.' + metric}/>
        {~metricWithPopover.indexOf(metric)
        ? (<span className="popover-content">
          <FormattedMessage id={'analytics.popover.' + metric}/>
        </span>)
        : null
        }
      </span>
    )
  }

  renderIcon = id => 
    id === "count" ? (
      <span className="analytics__mode-count">
        <FormattedMessage  id='analytics.mode.count' defaultMessage="Count"/>
      </span>
    ):(
      <Svg id={id}/>
    )

  renderTools() {
    const {units, metric} = this.props
    const modes = this.getModes();
    const mode = this.getMode(modes);

    if (!modes) {
      return '';
    }
    return (
      <div onClick={this.onModeClick} style={{display: 'inline-block'}}>
        <RadioGroup onChange={this.onModeChange} value={mode.id || mode} className="mode-ctrl">
          {modes.length > 1 && modes.map((id, i) =>
            (
              <RadioButton onClick={this.onModeClick} key={i} value={id}>
                {this.renderIcon(id)}
              </RadioButton>)
          )}
        </RadioGroup>
        {units 
          && metric !== 'lead-closure-relative' 
          && <RadioGroup onChange={this.onUnitChange} value={this.state.unit} className="mode-ctrl">
            {units.map((id, i) =>
              (
                <RadioButton onClick={this.onModeClick} key={i} value={id}>
                  {this.renderIcon(id)}
                </RadioButton>)
            )}
        </RadioGroup>}
      </div>
    )
  }

  _renderChart() {
    const mode = this.getMode();
    const {metric} = this.props
    const _metric = metric === 'leads' && this.state.showScore? 'score': metric 
    if (mode === 'pie') {
      return (
        <PieChart 
          {...this.props} 
          metric={_metric}
          scoringTypes={this.state.scoringTypes}
          unit={_metric !== "lead-closure-relative"?  this.state.unit: 'count'} 
          mode={mode.id || mode} />)
    } else {
      return (
        <LineBarChart 
          {...this.props} 
          metric={_metric}
          scoringTypes={this.state.scoringTypes}
          toggleScoringType={this.toggleScoringType}
          scalePeriod={this.props.scalePeriod}
          intervals={this.props.intervals}
          unit={_metric !== "lead-closure-relative"?  this.state.unit: 'count'} 
          mode={mode.id || mode} />)
    }
  }

  render() {
    return (
      <Collapse defaultActiveKey={['main-chart']} className="main-chart">
        <Panel
          header={this.renderHeader()}
          key="main-chart"
        >
          {this._renderChart()}
        </Panel>
      </Collapse>
    )
  }
}

export default injectIntl(MainChart);


// WEBPACK FOOTER //
// ./src/components/dashboard/analytics/chart.js